import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { BLANK, ColumnMenu, compareValues, type SortDir } from "./ColumnMenu";
import { Icon } from "./Icon";
import { EmptyState, ErrorState, TableSkeleton } from "./States";
import { useColumnWidths } from "./useColumnWidths";

export interface Column<T> {
  key: string;
  header: string;
  /** O'ngga tekislash — miqdor va sanoq ustunlari uchun. */
  align?: "right";
  width?: string;
  render: (row: T) => ReactNode;
  /**
   * Saralash qiymati. Berilmasa katakdagi KO'RINADIGAN matn
   * olinadi — «17 soat oldin» kabi nisbiy sana uchun bu yetarli
   * emas, shuning uchun sana ustunlari asl qiymatni beradi.
   */
  value?: (row: T) => string | number | null | undefined;
  /** `false` — ustunda filtr/saralash yo'q (masalan amallar). */
  filterable?: boolean;
}

interface DataTableProps<T> {
  columns: Array<Column<T>>;
  rows: T[] | undefined;
  rowKey: (row: T, index: number) => string;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyHint?: string;
  /** Server ro'yxatni kesganini OCHIQ aytish uchun. */
  hasMore?: boolean;
  footer?: ReactNode;
  /**
   * Ustun kengliklari saqlanadigan kalit.
   *
   * Berilmasa ustun kalitlaridan yasaladi: shakli bir xil
   * jadvallar bitta sozlamani bo'lishadi va har bir chaqiruv
   * joyiga qo'lda nom yozish shart emas.
   */
  tableId?: string;
  /**
   * Qatorlarni checkbox bilan tanlash (masalan yorliqlarni tanlab
   * chop etish). Kalit — `rowKey`. Filtr bilan yashirilgan qator
   * tanlovdan chiqmaydi.
   */
  selection?: {
    selected: ReadonlySet<string>;
    onChange: (next: Set<string>) => void;
  };
  /**
   * Qatorni bosish — odatda tafsilot panelini ochadi. Qator ichidagi
   * tugma, havola va maydon bosilganda chaqirilmaydi: ular o'z
   * amalini bajaradi.
   */
  onRowClick?: (row: T) => void;
  /** Ochiq (tanlangan) qator — yon panel qaysi qatorga tegishli. */
  activeKey?: string;
  /**
   * Katta ro'yxat (o'n minglab qator): faqat ko'rinadigan oyna
   * chiziladi. Filtrlanadigan ustunlar `value` berishi SHART —
   * chizilmagan qatorning matni DOM da yo'q.
   */
  virtualize?: boolean;
  /** Filtr va saralashdan keyingi qatorlar (masalan eksport uchun). */
  onVisibleRows?: (rows: T[]) => void;
}

/** Virtual jadvalda oyna atrofida qo'shimcha chiziladigan qatorlar. */
const OVERSCAN = 20;

/**
 * Ma'lumot jadvali.
 *
 * Shisha EMAS: ombor administratori bu yerda raqam o'qiydi va
 * xiralashgan fon uni sekinlashtirardi. Shaffoflik navigatsiya va
 * dialogda qoladi.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  error,
  onRetry,
  emptyTitle = "Ma'lumot yo'q",
  emptyHint,
  hasMore,
  footer,
  tableId,
  selection,
  onRowClick,
  activeKey,
  virtualize,
  onVisibleRows,
}: DataTableProps<T>) {
  const keys = useMemo(() => columns.map((column) => column.key), [columns]);
  const sizing = useColumnWidths(tableId ?? keys.join(","), keys);

  // Excel kabi saralash va filtr. Ular BRAUZERDA, yuklangan qatorlar
  // ustida ishlaydi: sahifalangan ro'yxatda faqat ochiq sahifaga
  // qo'llanadi (poyada shu aytiladi).
  const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>(null);
  const [filters, setFilters] = useState<Record<string, Set<string>>>({});
  const [menu, setMenu] = useState<{ key: string; anchor: HTMLElement } | null>(null);

  // Katakdagi ko'rinadigan matn: ustunlar faqat `render` beradi, va
  // nom kabi qiymatlar keyinroq (alohida so'rov bilan) keladi.
  // Shuning uchun matn DOM dan o'qiladi va o'zgarsa yangilanadi.
  const body = useRef<HTMLTableSectionElement>(null);
  const [texts, setTexts] = useState<Map<string, Record<string, string>>>(() => new Map());
  const readTexts = useCallback(() => {
    const root = body.current;
    if (!root) return;
    setTexts((previous) => {
      let changed = false;
      const next = new Map(previous);
      root.querySelectorAll<HTMLTableRowElement>("tr[data-row]").forEach((tr) => {
        const id = tr.dataset.row!;
        const cells: Record<string, string> = {};
        tr.querySelectorAll<HTMLTableCellElement>("td[data-col]").forEach((td) => {
          cells[td.dataset.col!] = (td.textContent ?? "").replace(/\s+/g, " ").trim();
        });
        const old = previous.get(id);
        if (!old || Object.keys(cells).some((key) => old[key] !== cells[key])) {
          next.set(id, cells);
          changed = true;
        }
      });
      return changed ? next : previous;
    });
  }, []);

  useLayoutEffect(() => {
    readTexts();
    const root = body.current;
    if (!root) return;
    let frame = 0;
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(readTexts);
    });
    observer.observe(root, { subtree: true, childList: true, characterData: true });
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  });

  const keyed = useMemo(
    () => (rows ?? []).map((row, index) => ({ row, id: rowKey(row, index) })),
    [rows, rowKey],
  );
  // Ustun `value` bersa filtr undan ishlaydi (chizilmagan qator ham
  // filtrlanadi); bermasa — katakda ko'rinadigan matn.
  const valueColumns = useMemo(
    () => new Map(columns.filter((column) => column.value).map((column) => [column.key, column.value!])),
    [columns],
  );
  const textOf = useCallback(
    (id: string, key: string, row?: T) => {
      const value = row !== undefined ? valueColumns.get(key) : undefined;
      if (value) return String(value(row as T) ?? "").replace(/\s+/g, " ").trim();
      return texts.get(id)?.[key];
    },
    [texts, valueColumns],
  );

  const visible = useMemo(() => {
    const active = Object.entries(filters);
    let result = keyed.filter(({ id, row }) =>
      active.every(([key, allowed]) => {
        const value = textOf(id, key, row);
        // Hali chizilmagan qator (matni noma'lum) yashirilmaydi:
        // u chizilgach matni o'qiladi va filtr qayta qo'llanadi.
        return value === undefined || allowed.has(value || BLANK);
      }),
    );
    if (sort) {
      const column = columns.find((item) => item.key === sort.key);
      const valueOf = (entry: { row: T; id: string }) =>
        column?.value ? column.value(entry.row) : textOf(entry.id, sort.key, entry.row);
      result = [...result].sort((a, b) => {
        const order = compareValues(valueOf(a), valueOf(b));
        return sort.dir === "asc" ? order : -order;
      });
    }
    return result;
  }, [keyed, filters, sort, columns, textOf]);

  const menuValues = useMemo(() => {
    if (!menu) return [];
    const set = new Set<string>();
    for (const { id, row } of keyed) set.add(textOf(id, menu.key, row) || BLANK);
    return [...set].sort(compareValues);
  }, [menu, keyed, textOf]);

  // Ota-komponent `columns` ni har renderda yangidan quradi — `visible`
  // ham har safar yangi massiv. Qatorlar O'ZGARGANDAGINA xabar beriladi,
  // aks holda ota holati → render → yangi `visible` cheksiz aylanadi.
  const reported = useRef<T[] | null>(null);
  useEffect(() => {
    if (!onVisibleRows) return;
    const previous = reported.current;
    if (previous && previous.length === visible.length && visible.every(({ row }, i) => previous[i] === row)) return;
    reported.current = visible.map(({ row }) => row);
    onVisibleRows(reported.current);
  }, [visible, onVisibleRows]);

  // Virtual oyna: aylantirish holati va o'rtacha qator balandligi
  // (chizilgan qatorlardan o'lchanadi — nom ostida brend bo'lsa qator
  // balandroq). Balandlik BIR MARTA o'lchanadi: har oynada qayta
  // o'lchansa, oyna o'zgarib boshqa qatorlar chiziladi, o'rtacha yana
  // o'zgaradi — ro'yxat oxirida cheksiz tebranardi.
  const scroller = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewport, setViewport] = useState(800);
  const [rowHeight, setRowHeight] = useState(44);
  const measured = useRef(false);
  useLayoutEffect(() => {
    if (!virtualize) return;
    const root = body.current;
    const box = scroller.current;
    if (box && box.clientHeight && Math.abs(box.clientHeight - viewport) > 1) setViewport(box.clientHeight);
    if (!root || measured.current) return;
    const rendered = root.querySelectorAll<HTMLTableRowElement>("tr[data-row]");
    if (rendered.length < Math.min(10, visible.length) || rendered.length === 0) return;
    let sum = 0;
    rendered.forEach((tr) => (sum += tr.offsetHeight));
    const average = Math.round(sum / rendered.length);
    if (average <= 0) return;
    measured.current = true;
    setRowHeight(average);
  });
  const frame = useRef(0);
  const onScroll = useCallback(() => {
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => setScrollTop(scroller.current?.scrollTop ?? 0));
  }, []);
  const start = virtualize ? Math.max(0, Math.floor(scrollTop / rowHeight) - OVERSCAN) : 0;
  const end = virtualize
    ? Math.min(visible.length, Math.ceil((scrollTop + viewport) / rowHeight) + OVERSCAN)
    : visible.length;
  const windowRows = virtualize ? visible.slice(start, end) : visible;
  const span = columns.length + (selection ? 1 : 0);

  const filtered = Object.keys(filters).length > 0;
  const closeMenu = useCallback(() => setMenu(null), []);

  if (error) {
    return (
      <div className="table-wrap">
        <ErrorState error={error} onRetry={onRetry} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="table-wrap">
        <TableSkeleton columns={Math.min(columns.length, 6)} />
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="table-wrap">
        <EmptyState title={emptyTitle} hint={emptyHint} />
        {/* Poya BO'SH sahifada ham qoladi: sahifalashda oxirgi
            sahifa bo'sh chiqishi mumkin va «Oldingi» tugmasi
            yo'qolsa xodim ortga qaytolmasdi. */}
        {footer && <div className="table-foot">{footer}</div>}
      </div>
    );
  }

  return (
    <div className="table-wrap">
      <div className="table-scroll" ref={scroller} onScroll={virtualize ? onScroll : undefined}>
        {/* Kenglik qotirilganda `table-layout: fixed` kerak:
            avtomatik joylashuvda brauzer sudralgan ustunni ham
            o'z bilganicha siqib qo'yardi. */}
        <table className={sizing.resized ? "data fixed" : "data"}>
          {sizing.resized && (
            <colgroup>
              {selection && <col style={{ width: "44px" }} />}
              {columns.map((column) => (
                <col
                  key={column.key}
                  style={
                    sizing.widths[column.key]
                      ? { width: `${sizing.widths[column.key]}px` }
                      : undefined
                  }
                />
              ))}
            </colgroup>
          )}
          <thead>
            <tr ref={sizing.headRow}>
              {selection && (
                <th className="select-col">
                  <input
                    type="checkbox"
                    aria-label="Ko'rinayotganlarning hammasini tanlash"
                    checked={visible.length > 0 && visible.every(({ id }) => selection.selected.has(id))}
                    onChange={(event) => {
                      const next = new Set(selection.selected);
                      for (const { id } of visible) {
                        if (event.target.checked) next.add(id);
                        else next.delete(id);
                      }
                      selection.onChange(next);
                    }}
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={column.align === "right" ? "right" : undefined}
                  style={
                    !sizing.resized && column.width ? { width: column.width } : undefined
                  }
                >
                  <span className="th-inner">
                    {column.header}
                    {sort?.key === column.key && (
                      <span className="th-sort" aria-hidden>
                        {sort.dir === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                    {column.header !== "" && column.filterable !== false && (
                      <button
                        type="button"
                        className={`th-filter${filters[column.key] ? " active" : ""}`}
                        aria-label={`${column.header}: saralash va filtr`}
                        title="Saralash va filtr"
                        onClick={(event) => {
                          const anchor = event.currentTarget;
                          setMenu((open) =>
                            open?.key === column.key ? null : { key: column.key, anchor },
                          );
                        }}
                      >
                        <Icon name="filter" size={12} />
                      </button>
                    )}
                  </span>
                  {/* Sudrash dastasi: chekkada, matnni to'smaydi.
                      Ikki marta bosish — shu jadvalning hamma
                      kengliklarini tiklaydi. */}
                  <span
                    className="col-grip"
                    role="separator"
                    aria-label={`${column.header} ustuni kengligi`}
                    onPointerDown={(event) => sizing.startResize(column.key, event)}
                    onDoubleClick={sizing.reset}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody ref={body}>
            {virtualize && start > 0 && (
              <tr aria-hidden style={{ height: start * rowHeight }}>
                <td colSpan={span} style={{ padding: 0, border: 0 }} />
              </tr>
            )}
            {windowRows.map(({ row, id }) => (
              <tr
                key={id}
                data-row={id}
                className={
                  [
                    selection?.selected.has(id) || activeKey === id ? "selected" : "",
                    onRowClick ? "clickable" : "",
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
                tabIndex={onRowClick ? 0 : undefined}
                onClick={
                  onRowClick
                    ? (event) => {
                        const target = event.target as HTMLElement;
                        if (target.closest("button, a, input, select, label")) return;
                        onRowClick(row);
                      }
                    : undefined
                }
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (event.key === "Enter" && event.target === event.currentTarget) {
                          onRowClick(row);
                        }
                      }
                    : undefined
                }
              >
                {selection && (
                  <td className="select-col">
                    <input
                      type="checkbox"
                      aria-label="Tanlash"
                      checked={selection.selected.has(id)}
                      onChange={() => {
                        const next = new Set(selection.selected);
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                        selection.onChange(next);
                      }}
                    />
                  </td>
                )}
                {columns.map((column) => (
                  <td
                    key={column.key}
                    data-col={column.key}
                    className={column.align === "right" ? "right" : undefined}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
            {virtualize && end < visible.length && (
              <tr aria-hidden style={{ height: (visible.length - end) * rowHeight }}>
                <td colSpan={span} style={{ padding: 0, border: 0 }} />
              </tr>
            )}
          </tbody>
        </table>
        {visible.length === 0 && (
          <EmptyState title="Filtrga mos qator yo'q" hint="Ustun filtrini o'zgartiring yoki tozalang." />
        )}
      </div>

      {menu && (
        <ColumnMenu
          anchor={menu.anchor}
          title={columns.find((column) => column.key === menu.key)?.header ?? ""}
          values={menuValues}
          selected={filters[menu.key]}
          sort={sort?.key === menu.key ? sort.dir : undefined}
          onSort={(dir) => setSort(dir ? { key: menu.key, dir } : null)}
          onFilter={(next) =>
            setFilters((current) => {
              const copy = { ...current };
              if (next) copy[menu.key] = next;
              else delete copy[menu.key];
              return copy;
            })
          }
          onClose={closeMenu}
        />
      )}

      {(filtered || sort) && (
        <div className="table-foot">
          <span className="table-foot-note">
            {filtered ? `Filtr: ${visible.length} / ${keyed.length} qator` : "Saralangan"}
            {(hasMore || footer) && !virtualize ? " — faqat shu sahifada" : ""}
          </span>
          <button
            type="button"
            className="btn btn-text btn-sm"
            onClick={() => {
              setFilters({});
              setSort(null);
            }}
          >
            <Icon name="close" size={14} />
            Filtr va saralashni tozalash
          </button>
        </div>
      )}

      {sizing.resized && (
        <button
          type="button"
          className="btn btn-text btn-sm col-reset"
          onClick={sizing.reset}
        >
          Ustun kengliklarini tiklash
        </button>
      )}

      {(hasMore || footer) && (
        <div className="table-foot">
          {/* Server ro'yxatni kesgani YASHIRILMAYDI: foydalanuvchi
              hamma narsani ko'rdim deb o'ylamasligi kerak. Poyada
              sahifa o'tkazgich bo'lsa bu ogohlantirish aytilmaydi —
              u yerda qolgan qatorlarga yo'l bor. */}
          {hasMore && !footer && (
            <span className="table-foot-note">
              Ro'yxat kesildi — filtr bilan toraytiring
            </span>
          )}
          {footer}
        </div>
      )}
    </div>
  );
}
